import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest, postRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import PeriodFilter, { periodRange } from "../../Component/PeriodFilter";
import Pill from "../../Component/Pill";
import TablePager from "../Users/components/TablePager";
import AffiliateLayout, { affiliateName, inr, nameInitials, tierRates } from "./components/AffiliateLayout";
import AffiliateDrawer from "./components/AffiliateDrawer";
import AffiliateCommissionModal from "./modal/AffiliateCommissionModal";
import AddPayoutModal from "./modal/AddPayoutModal";

// Interface for Stats (/affiliate/affiliate-dashboard-summary)
interface StatItem {
  value: number;
  growth: number;
}

interface AffiliateStats {
  totalAffiliates: StatItem;
  totalConversions: StatItem;
  totalCommission: StatItem;
  summary?: { avgCommission: number | string; totalClicks: number; conversionRate: number | string; avgPayout: number | string };
  totals?: { totalAffiliates: number; totalConversions: number; totalCommission: number; pendingPayout: number };
}

const EMPTY_STAT: StatItem = { value: 0, growth: 0 };

const AffiliateAccount = () => {
  const [affiliateList, setAffiliateList] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalAffiliates, setTotalAffiliates] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);

  // Drawer & Modals
  const [viewId, setViewId] = useState<string | null>(null);
  const [drawerRefresh, setDrawerRefresh] = useState(0);
  const [selectedAffiliate, setSelectedAffiliate] = useState<any>(null);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  // Stats State
  const [stats, setStats] = useState<AffiliateStats>({
    totalAffiliates: EMPTY_STAT,
    totalConversions: EMPTY_STAT,
    totalCommission: EMPTY_STAT,
  });

  // Filters
  const [period, setPeriod] = useState("d30");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [tabCounts, setTabCounts] = useState({ all: 0, payoutDue: 0, bankPending: 0 });

  // Fetch Affiliate List
  const getAffiliateList = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/affiliate/affiliate-list?page=${page}&limit=${pageSize}&activeTab=${activeTab}&search=${encodeURIComponent(searchQuery)}`
      );

      if (response.status === 'success') {
        setAffiliateList(response.data);
        setTotalPages(response.pagination.totalPages || 1);
        setTotalAffiliates(response.pagination.total);
        if (response.tabCounts) {
          setTabCounts(response.tabCounts);
        }
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading affiliates!");
    } finally {
      setIsLoader(false);
    }
  }, [page, pageSize, activeTab, searchQuery]);

  // Fetch Stats (follow the selected period)
  const fetchStats = useCallback(async () => {
    const [from, to] = periodRange(period, customFrom, customTo);
    if (period === "custom" && (!from || !to)) return;
    const range = period === "all"
      ? "range=all"
      : `range=custom&from=${from!.format('YYYY-MM-DD')}&to=${to!.format('YYYY-MM-DD')}`;
    try {
      const response = await getRequest(`/affiliate/affiliate-dashboard-summary?${range}`);
      if (response.status === 'success' && response.stats) {
        setStats(response.stats);
      } else if (response.status === 'session_expired') {
        logout();
      }
    } catch (error) {
      console.error("Failed to load affiliate stats.");
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    getAffiliateList();
  }, [getAffiliateList]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const handleDirectLogin = async (affiliateId: string) => {
    try {
      const response = await postRequest(`/affiliate/admin-login-as-affiliate`, { user_id: affiliateId });
      if (response.status === 'success') {
        toast.success("Opening affiliate dashboard...");
        window.open(`https://affiliates.uomox.com/auto-login?token=${response.token}`, '_blank');
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message || "Failed to login as this affiliate");
      }
    } catch (error) {
      toast.error("Something went wrong during direct login.");
    }
  };

  const openTierModal = (affiliate: any) => { setSelectedAffiliate(affiliate); setShowTierModal(true); };
  const openPayoutModal = (affiliate: any = null) => { setSelectedAffiliate(affiliate); setShowPayoutModal(true); };

  // after a tier / payout change: reload the list, stats and the open drawer
  const refreshAll = () => {
    getAffiliateList();
    fetchStats();
    setDrawerRefresh(n => n + 1);
  };

  const changeTab = (t: string) => { setActiveTab(t); setPage(1); };

  const resetFilters = () => {
    setSearchText("");
    changeTab("All");
  };

  const chips: [string, string, number][] = [
    ["All", "All", tabCounts.all],
    ["PayoutDue", "Payout due", tabCounts.payoutDue],
    ["BankPending", "Bank details pending", tabCounts.bankPending],
  ];

  // All time has no previous period, so no growth %
  const isAll = period === "all";
  const totals = stats.totals;
  const summary = stats.summary;

  return (
    <AffiliateLayout
      loading={isLoader}
      actions={<>
        <PeriodFilter
          allTime
          value={period}
          onChange={setPeriod}
          from={customFrom}
          to={customTo}
          onRangeChange={(f, t) => { setCustomFrom(f); setCustomTo(t); }}
        />
        {/* <button type="button" className="cx-btn cx-btn-primary" onClick={() => openPayoutModal()}>
          <i className="ri-bank-line"></i>Create payout
        </button> */}
      </>}
      stats={[
        { label: "Total affiliates", value: totals?.totalAffiliates ?? 0, icon: "ri-share-forward-line", delta: isAll ? "All time" : `+${stats.totalAffiliates.value} joined this period` },
        { label: "Referred clients (WABA live)", value: stats.totalConversions.value, icon: "ri-group-line", delta: isAll ? "All time" : stats.totalConversions.growth },
        { label: "Commission earned", value: inr(stats.totalCommission.value, 0), icon: "ri-exchange-dollar-line", delta: isAll ? "All time" : stats.totalCommission.growth },
        { label: "Pending payout", value: inr(totals?.pendingPayout ?? 0, 0), icon: "ri-bank-line", delta: `${tabCounts.payoutDue} affiliates due` },
        { label: "Click → client rate", value: `${summary?.conversionRate ?? 0}%`, icon: "ri-cursor-line", delta: `${Number(summary?.totalClicks || 0).toLocaleString("en-IN")} clicks this month` },
      ]}
    >
      <div className="cx-card">
        <div className="cx-toolbar">
          <div className="cx-chips">
            {chips.map(([k, l, n]) => (
              <button type="button" key={k} className={`cx-chip ${activeTab === k ? "on" : ""}`} onClick={() => changeTab(k)}>
                {l} <b>{n || 0}</b>
              </button>
            ))}
          </div>
          <div className="sp"></div>
          <input
            className="cx-input"
            type="search"
            placeholder="Search name, email, company, code…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
            <i className="ri-refresh-line"></i>Reset
          </button>
        </div>

        <div className="cx-tbl-wrap">
          <table className="cx-tbl">
            <thead>
              <tr>
                <th>Affiliate</th>
                <th>Code</th>
                <th>Tier</th>
                <th className="c">Clicks</th>
                <th className="c">Clients</th>
                <th className="r">Earned</th>
                <th className="r">Pending</th>
                <th>Bank</th>
                <th>Joined</th>
                <th className="c">Actions</th>
              </tr>
            </thead>
            <tbody>
              {affiliateList && affiliateList.length > 0 ? (
                affiliateList.map((item) => {
                  const name = affiliateName(item);
                  const tier = item.affiliate_referral_commission_data;
                  const pending = Number(item.pending_commission || 0);
                  return (
                    <tr key={item._id} className="click" onClick={() => setViewId(item._id)}>
                      <td>
                        <div className="cx-who">
                          <div className="av">{nameInitials(name)}</div>
                          <div>
                            <b>{name}</b>
                            <small>{item.affiliate_companyName ? `${item.affiliate_companyName} · ` : ""}<span className="mono">#{item.affiliate_unique_id || item.affiliate_id}</span></small>
                          </div>
                        </div>
                      </td>
                      <td className="mono">{item.affiliate_referral_code || <span className="muted">—</span>}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button type="button" title="Change commission tier" onClick={() => openTierModal(item)}>
                          {tier?.commission_title
                            ? <Pill tone="blue">{tier.commission_title} · {tierRates(tier)}</Pill>
                            : <Pill tone="none">Not set</Pill>}
                        </button>
                      </td>
                      <td className="c mono">{Number(item.affiliate_referral_hits || 0).toLocaleString("en-IN")}</td>
                      <td className="c mono" title={`${item.converted_user || 0} of ${item.total_user || 0} connected WABA`}>
                        {item.total_user || 0}<span className="muted fs12"> · {item.converted_user || 0} live</span>
                      </td>
                      <td className="r mono">{inr(item.total_commission)}</td>
                      <td className={`r mono ${pending > 0 ? "down" : "muted"}`} style={pending > 0 ? { fontWeight: 600 } : undefined}>{inr(pending)}</td>
                      <td>
                        {item.has_bank
                          ? <Pill tone="ok">{item.bank_name || "Added"}</Pill>
                          : <Pill tone="warn">Missing</Pill>}
                      </td>
                      <td className="fs12 muted">{item.createdAt ? moment(item.createdAt).format("DD MMM YYYY") : "—"}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="cx-ra">
                          <button type="button" className="v" title="View details" onClick={() => setViewId(item._id)}>
                            <i className="ri-eye-line"></i>
                          </button>
                          <button type="button" title="Login as affiliate" onClick={() => handleDirectLogin(item._id)}>
                            <i className="ri-login-circle-line"></i>
                          </button>
                          {/* only when there is something to pay; kept in place so the columns line up */}
                          {/* <button type="button" title="Create payout" style={pending > 0 ? undefined : { visibility: "hidden" }} onClick={() => openPayoutModal(item)}>
                            <i className="ri-bank-line"></i>
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10}>
                    <div className="cx-empty">
                      <i className="ri-share-forward-line"></i>
                      {isLoader ? "Loading…" : <>No affiliates match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button></>}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePager
          page={page}
          totalPages={totalPages}
          total={totalAffiliates}
          shown={affiliateList.length}
          pageSize={pageSize}
          label="affiliates"
          note={summary ? `This month · avg commission ${inr(summary.avgCommission)} · avg payout ${inr(summary.avgPayout)}` : undefined}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </div>

      <AffiliateDrawer
        affiliateId={viewId}
        refreshKey={drawerRefresh}
        onClose={() => setViewId(null)}
        onChangeTier={openTierModal}
        onCreatePayout={openPayoutModal}
        onLogin={handleDirectLogin}
      />

      {/* Modals */}
      <AffiliateCommissionModal show={showTierModal} onHide={() => setShowTierModal(false)} affiliateData={selectedAffiliate} onSuccess={refreshAll} />
      <AddPayoutModal show={showPayoutModal} onHide={() => setShowPayoutModal(false)} affiliate={selectedAffiliate} onSuccess={refreshAll} />
    </AffiliateLayout>
  );
};

export default AffiliateAccount;
